import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { carImportApi, CarImport, CarImportSearchParams, PaginatedResponse } from '../api/apiClient';

export const useCarImports = (params: CarImportSearchParams = {}) => {
  return useQuery({
    queryKey: ['carImports', params],
    queryFn: () => carImportApi.getAll(params),
  });
};

export const useCarImport = (id: number) => {
  return useQuery({
    queryKey: ['carImport', id],
    queryFn: () => carImportApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateCarImport = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: carImportApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carImports'] });
    },
  });
};

export const useUpdateCarImport = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CarImport> }) => 
      carImportApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['carImports'] });
      queryClient.invalidateQueries({ queryKey: ['carImport', id] });
    },
  });
};

export const useDeleteCarImport = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => carImportApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carImports'] });
    },
  });
};

export const useUploadCarImports = () => {
  const queryClient = useQueryClient();
  
  return {
    uploadFile: useMutation({
      mutationFn: (file: File) => carImportApi.uploadFile(file),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['carImports'] });
      },
    }),
  };
};

export const useAverageTax = (searchParams: CarImportSearchParams) => {
  return useQuery({
    queryKey: ['averageTax', searchParams],
    queryFn: async () => {
      const response = await carImportApi.getAverageTax(searchParams);
      return response.data;
    },
    enabled: true,
  });
};
